import scheduleConfig from '../config/schedule'

/**
 * Records and replays a scroll position across back-navigation, keyed by URL and history entry.
 *
 * See docs/decisions/CDR-003-schedule-scroll-restoration.md
 */

export interface RestorePoint {
  /** Matched against `data-schedule-item` to find the element again. */
  itemId: string
  /** Where the element sat in the viewport when the user left. */
  viewportTop: number
  /** The history entry the user left from. `undefined` for the entry the app was loaded on. */
  entryKey: string | undefined
  /** The schedule's search text, which lives in component state rather than in the URL. */
  searchText?: string
}

const ITEM_SELECTOR = '[data-schedule-item]'

/** How often (ms) to look for the anchor while it is still missing. */
const FIND_INTERVAL_MS = 100

/** A settled anchor still moves by sub-pixel amounts on fractional layouts. */
const SETTLED_DELTA_PX = 1

/** Frames of "asked to scroll, nothing moved, page no taller" before the anchor counts as unreachable. */
const STALLED_FRAMES = 10

/** Events that hand the page back to the user. */
const CANCEL_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const

const restorePoints = new Map<string, RestorePoint>()

/** The key currently being restored, or null. */
let restoringKey: string | null = null

/** Canonical key for the URL the browser is on, read off `window` rather than off the router. */
export function getScrollKey(): string {
  const { pathname, search } = window.location
  const params = new URLSearchParams(search)
  params.sort()
  const query = params.toString()
  return query ? `${pathname}?${query}` : pathname
}

export function saveRestorePoint(point: RestorePoint): void {
  restorePoints.set(getScrollKey(), point)
}

/** Forgets this URL's saved position, for a departure it no longer describes. */
export function clearRestorePoint(): void {
  restorePoints.delete(getScrollKey())
}

/**
 * The position saved for this URL, if it was saved on the history entry given.
 *
 * @param entryKey `location.key` for the entry being returned to.
 */
export function getRestorePoint(entryKey: string | undefined): RestorePoint | undefined {
  const point = restorePoints.get(getScrollKey())
  return point?.entryKey === entryKey ? point : undefined
}

export function hasRestorePoint(entryKey: string | undefined): boolean {
  return getRestorePoint(entryKey) !== undefined
}

/** Whether a restore is in flight for the URL the browser is on now. */
export function isRestoringCurrentLocation(): boolean {
  return restoringKey !== null && restoringKey === getScrollKey()
}

/** Resets module state between tests. */
export function resetScrollRestoration(): void {
  restorePoints.clear()
  restoringKey = null
}

const findAnchor = (itemId: string): HTMLElement | null =>
  Array.from(document.querySelectorAll<HTMLElement>(ITEM_SELECTOR)).find(
    (element) => element.dataset.scheduleItem === itemId
  ) ?? null

/**
 * Scrolls the anchor back to where it was, correcting by delta each frame while the page settles.
 *
 * See docs/decisions/CDR-003-schedule-scroll-restoration.md
 *
 * @returns a function that cancels the restore. Safe to call more than once.
 */
export function restoreToAnchor(point: RestorePoint): () => void {
  restoringKey = getScrollKey()

  const key = restoringKey
  const deadline = Date.now() + scheduleConfig.scrollRestore.timeoutMs

  let anchor: HTMLElement | null = null
  let frame: number | null = null
  let lastLookedAt = 0
  let stalledFrames = 0
  let lastScrollHeight = -1
  let done = false

  /** Hands the page back to the user, unless the anchor has yet to render. */
  function onUserInput(): void {
    if (!anchor?.isConnected) return
    finish()
  }

  function finish(): void {
    if (done) return
    done = true
    if (frame !== null) window.cancelAnimationFrame(frame)
    CANCEL_EVENTS.forEach((type) => window.removeEventListener(type, onUserInput))
    // Release the flag only when nothing newer has claimed it.
    if (restoringKey === key) restoringKey = null
  }

  const step = (): void => {
    if (done) return

    if (Date.now() >= deadline) {
      finish()
      return
    }

    // Looked up again when the node goes away, because a re-render can replace it.
    if (!anchor?.isConnected && Date.now() - lastLookedAt >= FIND_INTERVAL_MS) {
      lastLookedAt = Date.now()
      anchor = findAnchor(point.itemId)
    }

    if (!anchor?.isConnected) {
      frame = window.requestAnimationFrame(step)
      return
    }

    const delta = Math.round(anchor.getBoundingClientRect().top - point.viewportTop)

    // Nothing to correct this frame. Stay subscribed for shifts that land later.
    if (Math.abs(delta) <= SETTLED_DELTA_PX) {
      stalledFrames = 0
      frame = window.requestAnimationFrame(step)
      return
    }

    const before = window.scrollY
    window.scrollBy(0, delta)

    // A scroll that moved nothing means a boundary. Give up only once the page stops growing too.
    if (window.scrollY === before) {
      const scrollHeight = document.documentElement.scrollHeight
      stalledFrames = scrollHeight === lastScrollHeight ? stalledFrames + 1 : 0
      lastScrollHeight = scrollHeight

      if (stalledFrames >= STALLED_FRAMES) {
        finish()
        return
      }
    } else {
      stalledFrames = 0
    }

    frame = window.requestAnimationFrame(step)
  }

  CANCEL_EVENTS.forEach((type) => window.addEventListener(type, onUserInput, { passive: true }))
  frame = window.requestAnimationFrame(step)

  return finish
}
